/**
 * EMME HOME - Multi-Image Variant Gallery
 * =========================================
 * Permite mostrar múltiples imágenes por variante usando metafields de Shopify.
 *
 * REQUISITO: Crear metafield en Shopify Admin → Settings → Custom data → Variants:
 *   Namespace:  custom
 *   Key:        variant_images
 *   Type:       File reference  →  List of files (list.file_reference)
 *
 * FUNCIONAMIENTO:
 *  1. El snippet product-media-gallery-content.liquid incrusta un JSON con todas
 *     las imágenes de metafield de cada variante dentro del elemento <media-gallery>.
 *  2. Este módulo intercepta el evento 'variant:update' en fase CAPTURE,
 *     antes de que media-gallery.js reemplace el elemento.
 *  3. Inyecta los slides de metafield en el HTML descargado ANTES de que
 *     media-gallery.js lo aplique al DOM.
 *  4. En la carga inicial también inyecta las imágenes de la variante activa.
 */

// ─── Constantes ───────────────────────────────────────────────────────────────

const DATA_SCRIPT_ID    = 'emme-variant-images-data';
const SLIDE_CLASS       = 'emme-variant-slide';
const THUMB_CLASS       = 'emme-variant-thumb';
const GRID_ITEM_CLASS   = 'emme-variant-grid-item';
const VARIANT_UPDATE_EVENT = 'variant:update';

// ─── Utilidades de URL ────────────────────────────────────────────────────────

/**
 * Construye un srcset para la CDN de Shopify a partir de una URL base.
 * Preserva el parámetro ?v=... de versión de caché.
 * @param {string} url - URL de la imagen con parámetros existentes
 * @returns {string} srcset string
 */
function buildSrcset(url) {
  const widths = [240, 352, 832, 1200, 1600, 1920];
  // Quitar el parámetro width si ya existe, mantener el resto (?v=xxx)
  const baseUrl = url.replace(/([?&])width=\d+(&?)/, (_, prefix, suffix) => {
    if (prefix === '?' && suffix === '&') return '?';
    if (prefix === '&' && suffix === '&') return '&';
    return prefix === '?' ? '' : prefix;
  }).replace(/[?&]$/, '');

  const sep = baseUrl.includes('?') ? '&' : '?';
  return widths.map(w => `${baseUrl}${sep}width=${w} ${w}w`).join(', ');
}

// ─── Lectura de datos ─────────────────────────────────────────────────────────

/**
 * Lee el JSON de imágenes de variante desde el script tag embebido en la galería.
 * @param {Element} gallery - El elemento <media-gallery>
 * @returns {Record<string, Array>|null}
 */
function parseVariantData(gallery) {
  const el = gallery.querySelector(`#${DATA_SCRIPT_ID}`);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[EMME] Error al parsear datos de variante:', e);
    return null;
  }
}

/**
 * Obtiene el ID de la variante actualmente seleccionada en el formulario de producto.
 * @returns {string|null}
 */
function getActiveVariantId() {
  return (
    document.querySelector('product-form-component input[name="id"]')?.value ??
    document.querySelector('[action*="/cart/add"] input[name="id"]')?.value ??
    null
  );
}

// ─── Construcción de HTML ─────────────────────────────────────────────────────

/**
 * Genera el HTML de un slide de carousel para una imagen de metafield.
 * @param {{ url: string, url_thumb: string, alt: string, aspect_ratio: number, width?: number, height?: number }} image
 * @param {number} index - Índice absoluto del slide (incluyendo slides anteriores)
 * @returns {string}
 */
function createSlideHTML(image, index) {
  const ar      = (image.aspect_ratio && image.aspect_ratio > 0) ? image.aspect_ratio : 1.0;
  const srcset  = buildSrcset(image.url);
  const alt     = (image.alt || '').replace(/"/g, '&quot;');
  const loading = index === 0 ? 'eager' : 'lazy';
  const fetchp  = index === 0 ? 'high'  : 'auto';
  const dims    = image.width && image.height
    ? `width="${image.width}" height="${image.height}"`
    : '';

  return `<slideshow-slide
    ref="slides[]"
    aria-hidden="${index === 0 ? 'false' : 'true'}"
    class="${SLIDE_CLASS}"
    style="--slideshow-timeline: --slide-${index}; --product-media-fit: cover; --grid-template-rows: 50dvh 1fr; --grid-template-rows-desktop: 70dvh 1fr;"
  ><div class="product-media-container media-fit-cover product-media-container--image"
  ><div class="product-media" style="--ratio: ${ar};" data-media-id="emme-meta-${index}"
  ><img
    src="${image.url}"
    srcset="${srcset}"
    sizes="(min-width: 1200px) 700px, (min-width: 750px) 50vw, 100vw"
    class="product-media__image"
    loading="${loading}"
    fetchpriority="${fetchp}"
    alt="${alt}"
    ${dims}
  ></div></div></slideshow-slide>`;
}

/**
 * Genera el HTML de un item de grid para una imagen de metafield.
 * @param {{ url: string, alt: string, aspect_ratio: number }} image
 * @param {number} index
 * @returns {string}
 */
function createGridItemHTML(image, index) {
  const ar     = (image.aspect_ratio && image.aspect_ratio > 0) ? image.aspect_ratio : 1.0;
  const srcset = buildSrcset(image.url);
  const alt    = (image.alt || '').replace(/"/g, '&quot;');

  return `<li
    ref="media[]"
    class="product-media-container media-fit-cover product-media-container--image ${GRID_ITEM_CLASS}"
    style="--media-preview-ratio: ${ar};"
  ><div class="product-media" style="--ratio: ${ar};" data-media-id="emme-meta-grid-${index}"
  ><img
    src="${image.url}"
    srcset="${srcset}"
    sizes="(min-width: 1200px) 50vw, 100vw"
    class="product-media__image"
    loading="${index === 0 ? 'eager' : 'lazy'}"
    alt="${alt}"
  ></div></li>`;
}

/**
 * Genera el HTML de un botón de thumbnail para slideshow-controls.
 * @param {{ url_thumb: string, url: string, alt: string, aspect_ratio: number }} image
 * @param {number} index
 * @returns {string}
 */
function createThumbHTML(image, index) {
  const ar  = (image.aspect_ratio && image.aspect_ratio > 0) ? image.aspect_ratio : 1.0;
  const alt = (image.alt || `Imagen ${index + 1}`).replace(/"/g, '&quot;');
  // Usar url_thumb (width=144) igual que Shopify nativo
  const src = image.url_thumb || image.url;

  return `<button
    type="button"
    ref="thumbnails[]"
    class="slideshow-control button button-unstyled slideshow-controls__thumbnail ${THUMB_CLASS}"
    aria-label="${alt}"
    on:click="/select/${index}"
    style="--aspect-ratio: ${ar};"
    ${index === 0 ? 'aria-selected="true"' : ''}
  ><img src="${src}" loading="lazy" alt="${alt}" width="144" height="144"></button>`;
}

// ─── Lógica de inyección ──────────────────────────────────────────────────────

/**
 * Actualiza el CSS custom property --slideshow-timeline en slideshow-component.
 * @param {Element} gallery
 * @param {number} totalSlides
 */
function updateTimelineScope(gallery, totalSlides) {
  const comp = gallery.querySelector('slideshow-component');
  if (!comp) return;
  const scope = Array.from({ length: totalSlides }, (_, i) => `--slide-${i}`).join(', ');
  const currentStyle = comp.getAttribute('style') || '';
  const newStyle = currentStyle.replace(
    /--slideshow-timeline:\s*[^;]+;?/,
    `--slideshow-timeline: ${scope};`
  );
  comp.setAttribute('style', newStyle);
  if (comp.style) {
    comp.style.setProperty('--slideshow-timeline', scope);
  }
}

/**
 * REEMPLAZA todos los slides/thumbnails/grid por las imágenes del metafield.
 * Si la variante tiene imágenes en metafield → solo se ven esas.
 * Si no tiene → no se modifica nada (comportamiento estándar de Shopify).
 *
 * @param {Element} gallery - Elemento <media-gallery>
 * @param {Array} images - Array de objetos de imagen del metafield
 * @returns {boolean} true si se reemplazó
 */
function injectMetafieldImages(gallery, images) {
  if (!images || images.length === 0) return false;

  const slidesContainer = gallery.querySelector('slideshow-slides');
  if (!slidesContainer) return false;

  // ── 1. Eliminar TODOS los slides existentes de Shopify ──
  slidesContainer.querySelectorAll('slideshow-slide').forEach(el => el.remove());

  // ── 2. Insertar únicamente los slides del metafield ──
  const slidesHTML = images.map((img, i) => createSlideHTML(img, i)).join('');
  slidesContainer.insertAdjacentHTML('afterbegin', slidesHTML);

  // ── 3. Actualizar timeline scope ──
  updateTimelineScope(gallery, images.length);

  // ── 4. Reemplazar thumbnails si existen ──
  // Los botones están dentro de .slideshow-controls__thumbnails > button (igual que Shopify nativo)
  const thumbsDiv = gallery.querySelector('.slideshow-controls__thumbnails');
  if (thumbsDiv) {
    // Eliminar todos los botones existentes (todas las variantes)
    thumbsDiv.querySelectorAll('button').forEach(el => el.remove());
    const thumbsHTML = images.map((img, i) => createThumbHTML(img, i)).join('');
    thumbsDiv.insertAdjacentHTML('afterbegin', thumbsHTML);
  } else {
    // Fallback: si no existe el div, buscar directamente en el scroll-hint
    const thumbContainer = gallery.querySelector('.slideshow-controls__thumbnails-container');
    if (thumbContainer) {
      thumbContainer.querySelectorAll('button').forEach(el => el.remove());
      // Crear el div wrapper igual que Shopify
      const wrapper = document.createElement('div');
      wrapper.className = 'slideshow-controls__thumbnails';
      wrapper.innerHTML = images.map((img, i) => createThumbHTML(img, i)).join('');
      thumbContainer.insertAdjacentElement('afterbegin', wrapper);
    }
  }

  // ── 5. Reemplazar items del grid si existe ──
  const gridList = gallery.querySelector('.media-gallery__grid');
  if (gridList) {
    gridList.querySelectorAll('li').forEach(el => el.remove());
    const gridHTML = images.map((img, i) => createGridItemHTML(img, i)).join('');
    gridList.insertAdjacentHTML('afterbegin', gridHTML);
  }

  return true;
}

// ─── Función principal ────────────────────────────────────────────────────────

/**
 * Procesa una galería para el ID de variante dado.
 * Lee el JSON de metafield e inyecta imágenes si existen.
 * @param {Element} gallery
 * @param {string} variantId
 * @returns {boolean}
 */
function processGallery(gallery, variantId) {
  if (!gallery || !variantId) return false;

  const data = parseVariantData(gallery);
  if (!data) return false;

  const images = data[String(variantId)];
  if (!images || images.length === 0) return false;

  return injectMetafieldImages(gallery, images);
}

// ─── Carga inicial ────────────────────────────────────────────────────────────
// No se inyectan imágenes al cargar la página: el cliente quiere ver todas las
// fotos del producto en la carga inicial. Solo se filtran por variante cuando
// el usuario selecciona explícitamente una opción (evento variant:update).

// ─── Cambio de variante ───────────────────────────────────────────────────────
// Escuchamos en fase CAPTURE para ejecutar ANTES que media-gallery.js
// El evento 'variant:update' lleva en event.detail.data.html el Document completo
// de la página descargada. Modificamos ese Document antes de que media-gallery.js
// lo use para reemplazar el <media-gallery> del DOM.

document.addEventListener(VARIANT_UPDATE_EVENT, (event) => {
  const html      = event.detail?.data?.html;
  const variantId = event.detail?.resource?.id?.toString();

  if (!html || !variantId) return;

  const newGallery = html.querySelector('media-gallery');
  if (!newGallery) return;

  // Inyectar en el HTML descargado ANTES de que media-gallery.js lo aplique
  processGallery(newGallery, variantId);

}, { capture: true });
